import type { ColumnOrderState, ColumnPinningState, VisibilityState } from '@tanstack/react-table';
import { SpecialColumnsIds } from '../SpecialColumns';
import type { AnyColumnDef } from '../utils/Any';
import type { TableInfiniteDataSource } from '../utils/createTableInfiniteDataSource';
import { getColumnId } from '../utils/getColumnId';
import { makeFiltersOptional } from '../utils/makeFiltersOptional';
import type { TableState } from '../utils/TableState';
import { format } from 'date-fns';

const CSV_BOM = '\uFEFF';

const SPECIAL_COLUMN_IDS = new Set<string>([SpecialColumnsIds.ACTIONS, SpecialColumnsIds.STATE]);

type ExportTableToCsvParams = {
  dataSource: TableInfiniteDataSource;
  columns: AnyColumnDef[];
  filters: TableState['filters'];
  ordering: TableState['ordering'];
  visibility: VisibilityState;
  columnOrder: ColumnOrderState;
  columnPinning: ColumnPinningState;
  fileName: string;
};

export const exportTableToCsv = async ({
  dataSource,
  columns,
  filters,
  ordering,
  visibility,
  columnOrder,
  columnPinning,
  fileName,
}: ExportTableToCsvParams) => {
  const rows = await fetchAllTableData(dataSource, filters, ordering);
  const exportColumns = getExportableColumns(columns, visibility, columnOrder, columnPinning);
  const headers = exportColumns.map(getColumnHeader);
  const csvRows = rows.map((row) => exportColumns.map((column) => getCsvCellValue(row, column)));

  downloadCsv(buildCsv(headers, csvRows), fileName);
};

export const fetchAllTableData = async (
  dataSource: TableInfiniteDataSource,
  filters: TableState['filters'],
  ordering: TableState['ordering'],
) => {
  const page = await dataSource.queryFn({
    filters: makeFiltersOptional(filters),
    ordering: ordering ?? undefined,
  });

  return page.result.data ?? [];
};

export const getExportableColumns = (
  columns: AnyColumnDef[],
  visibility: VisibilityState,
  columnOrder: ColumnOrderState,
  columnPinning: ColumnPinningState,
) => {
  const columnById = new Map(columns.map((column) => [getColumnId(column), column] as const));
  const defaultOrder = columns.map((column) => getColumnId(column));
  const orderedIds =
    columnOrder.length > 0
      ? [...columnOrder.filter((id) => columnById.has(id)), ...defaultOrder.filter((id) => !columnOrder.includes(id))]
      : defaultOrder;

  const leftIds = columnPinning.left ?? [];
  const rightIds = columnPinning.right ?? [];
  const visuallyOrderedIds = [
    ...leftIds.filter((id) => orderedIds.includes(id)),
    ...orderedIds.filter((id) => !leftIds.includes(id) && !rightIds.includes(id)),
    ...rightIds.filter((id) => orderedIds.includes(id)),
  ];

  return visuallyOrderedIds.flatMap((id) => {
    const column = columnById.get(id);
    if (!column || SPECIAL_COLUMN_IDS.has(id) || visibility[id] === false || !canExportColumn(column)) {
      return [];
    }

    return [column];
  });
};

const canExportColumn = (column: AnyColumnDef) => {
  if (column.meta?.getCsvValue) {
    return true;
  }

  return hasAccessor(column);
};

const hasAccessor = (column: AnyColumnDef) => {
  if ('accessorFn' in column && typeof column.accessorFn === 'function') {
    return true;
  }

  return 'accessorKey' in column && typeof column.accessorKey === 'string';
};

export const getColumnHeader = (column: AnyColumnDef) => {
  if (typeof column.header === 'string' && column.header.length > 0) {
    return column.header;
  }

  return getColumnId(column);
};

export const getCsvCellValue = (row: unknown, column: AnyColumnDef) => {
  if (column.meta?.getCsvValue) {
    return column.meta.getCsvValue(row);
  }

  if ('accessorFn' in column && typeof column.accessorFn === 'function') {
    return formatCsvValue(column.accessorFn(row, 0));
  }

  if ('accessorKey' in column && typeof column.accessorKey === 'string') {
    return formatCsvValue((row as Record<string, unknown>)[column.accessorKey]);
  }

  return '';
};

export const formatCsvValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (Array.isArray(value)) {
    return value.map(formatCsvValue).filter(Boolean).join(', ');
  }

  if (typeof value === 'object' && 'name' in value && value.name != null) {
    return formatCsvValue(value.name);
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
};

export const buildCsv = (headers: string[], rows: string[][]) =>
  [headers, ...rows].map((line) => line.map(escapeCsvField).join(',')).join('\r\n');

const escapeCsvField = (value: string) => {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }

  return value;
};

export const downloadCsv = (csv: string, fileName: string) => {
  const blob = new Blob([`${CSV_BOM}${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const normalizedFileName = fileName.endsWith('.csv') ? fileName : `${fileName}.csv`;

  link.href = url;
  link.download = normalizedFileName;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const formatExportFileName = (name: string) => {
  const date = format(new Date(), 'dd.MM.yyyy_HH.mm');
  return `${name.replace(/ /g, '_').toLowerCase()}_${date}`;
};
